import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableContainer,
  TablePagination,
  Box,
  TableSortLabel,
} from "@mui/material";
import React, {
  useState,
  useEffect,
  ChangeEvent,
  useMemo,
  MouseEvent,
} from "react";
import Papa from "papaparse";
import { Toaster } from "ui-widgets";
import AlternateSearchInput from "datalign-module-template/components/search-input/AlternateSearchInput";
import { Order } from "datalign-module-template/models/order";
import { visuallyHidden } from "@mui/utils";

function descendingComparator(
  a: string[],
  b: string[],
  orderByIndex: number,
): number {
  const aCompare = a[orderByIndex]?.toLowerCase?.() ?? "";
  const bCompare = b[orderByIndex]?.toLowerCase?.() ?? "";
  if (bCompare < aCompare) return -1;
  if (bCompare > aCompare) return 1;
  return 0;
}

function getComparator(
  order: Order,
  orderByIndex: number,
): (a: string[], b: string[]) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderByIndex)
    : (a, b) => -descendingComparator(a, b, orderByIndex);
}

const CSVViewer = ({ csvContent }: { csvContent: string }) => {
  const [headers, setHeaders] = useState<string[]>([]);
  const [data, setData] = useState<string[][]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<number>(0);

  useEffect(() => {
    try {
      const parsed = Papa.parse(csvContent.trim(), { skipEmptyLines: true });

      if (!parsed?.data?.length) {
        console.warn("CSV has no data");
        setHeaders([]);
        setData([]);
        return;
      }

      const rawData = parsed.data as string[][];
      const filtered = rawData.filter((r) => Array.isArray(r) && r.length > 0);

      if (!filtered.length) {
        setHeaders([]);
        setData([]);
        return;
      }

      const maxCols = Math.max(...filtered.map((r) => r.length));
      const normalized = filtered.map((r) =>
        Array.from({ length: maxCols }, (_, i) => r[i] ?? ""),
      );

      const firstRow = normalized[0] ?? [];
      const secondRow = normalized[1] ?? [];

      const likelyHeader =
        !!secondRow.length &&
        firstRow.length > 0 &&
        firstRow.every((cell, i) => {
          if (cell == null) return false;
          const trimmed = String(cell).trim();
          if (trimmed === "") return false;
          if (!isNaN(Number(trimmed))) return false;
          if (trimmed.length > 100) return false;
          return trimmed !== String(secondRow[i] ?? "").trim();
        });

      const headers =
        likelyHeader && firstRow.length
          ? firstRow
          : Array.from({ length: maxCols }, (_, i) => `Column ${i + 1}`);

      setHeaders(headers);
      setData(likelyHeader ? normalized.slice(1) : normalized);
    } catch (err) {
      console.error("Error parsing CSV:", err);
      setHeaders([]);
      setData([]);
    }
  }, [csvContent]);

  useEffect(() => {
    setPage(0);
    if (!data.length) return;

    try {
      const filtered = data.filter((row) =>
        searchValue
          ? row.some((col) =>
              String(col).toLowerCase().includes(searchValue.toLowerCase()),
            )
          : true,
      );
      setRows(filtered);
    } catch (e) {
      Toaster.makeToast("Error while filtering table", "error");
      console.error(e);
      setRows(data);
    }
  }, [data, searchValue]);

  useEffect(() => {
    setPage(0);
    if (!data.length) return;

    try {
      const filtered = data.filter((row) =>
        searchValue
          ? row.some((col) =>
              String(col).toLowerCase().includes(searchValue.toLowerCase()),
            )
          : true,
      );
      setRows(filtered);
    } catch (e) {
      Toaster.makeToast("Error while filtering table", "error");
      console.error(e);
      setRows(data);
    }
  }, [data, searchValue]);

  const handleRequestSort = (_: MouseEvent<unknown>, property: string) => {
    const headerIndex = headers.findIndex((h) => h === property);
    const isAsc = orderBy === headerIndex && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(headerIndex);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(() => {
    const sorted = rows.slice().sort(getComparator(order, orderBy));
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [rows, page, rowsPerPage, order, orderBy]);

  if (!data.length) {
    return <Typography>No valid CSV data found.</Typography>;
  }

  return (
    <Box>
      <TableContainer>
        <AlternateSearchInput
          value={searchValue}
          onChange={(val) => setSearchValue(val)}
        />
        <Table aria-labelledby="tableTitle" size="medium" sx={{ m: 0 }}>
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell
                  key={`csv-header-${index}`}
                  align="left"
                  sortDirection={orderBy === index ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === index}
                    direction={orderBy === index ? order : "asc"}
                    onClick={(e) => handleRequestSort(e, header)}
                  >
                    {header}
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {headers.map((_, cellIndex) => (
                  <TableCell key={cellIndex}>{row[cellIndex] ?? ""}</TableCell>
                ))}
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={headers.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 1000, 10000]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default CSVViewer;
